import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { profileApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { User, Save, Camera, Mail, Phone, MapPin, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await profileApi.get();
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await profileApi.update({
        name: profile.name,
        phone: profile.phone || '',
        street_address: profile.street_address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        profile_image: profile.profile_image || null,
      });
      toast.success('Profile updated');
      checkAuth();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfile({ ...profile, profile_image: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const profileImage = profile?.profile_image || profile?.picture;

  const roleColors = {
    admin: 'bg-red-100 text-red-700',
    member: 'bg-blue-100 text-blue-700',
    visitor: 'bg-stone-100 text-stone-700'
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-gradient-to-r from-stone-800 to-stone-700 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">My Profile</h1>
          <p className="text-xl text-stone-300">Manage your personal information</p>
        </div>
      </section>

      <section className="py-8 bg-stone-50">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="p-6 md:p-8">
              {/* Profile Image */}
              <div className="flex flex-col items-center mb-8">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                    {profileImage ? (
                      <img src={profileImage} alt={profile?.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-14 w-14 text-stone-400" />
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 bg-amber-700 text-white rounded-full p-2 shadow-md hover:bg-amber-800 transition-colors"
                    data-testid="upload-profile-image-btn"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <h2 className="text-xl font-semibold text-stone-800 mt-4">{profile?.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={roleColors[profile?.role]}>{profile?.role}</Badge>
                  {profile?.role === 'visitor' && (
                    <span className="text-xs text-stone-500">Contact admin for member access</span>
                  )}
                </div>
              </div>

              {/* Profile Form */}
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> Full Name</Label>
                    <Input
                      value={profile?.name || ''}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</Label>
                    <Input value={profile?.email || ''} disabled className="bg-stone-50" />
                    <p className="text-xs text-stone-400 mt-1">Email cannot be changed</p>
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> Phone Number</Label>
                  <Input
                    value={profile?.phone || ''}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="(219) 555-0123"
                    data-testid="profile-phone-input"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-1 mb-2"><MapPin className="h-3.5 w-3.5" /> Home Address</Label>
                  <div className="space-y-3">
                    <Input
                      value={profile?.street_address || ''}
                      onChange={(e) => setProfile({ ...profile, street_address: e.target.value })}
                      placeholder="Street address"
                      data-testid="profile-street-input"
                    />
                    <div className="grid grid-cols-6 gap-3">
                      <div className="col-span-3">
                        <Input
                          value={profile?.city || ''}
                          onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                          placeholder="City"
                          data-testid="profile-city-input"
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          value={profile?.state || ''}
                          onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                          placeholder="State"
                          maxLength={2}
                          data-testid="profile-state-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={profile?.zip_code || ''}
                          onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                          placeholder="Zip code"
                          data-testid="profile-zip-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-amber-700 hover:bg-amber-800"
                  data-testid="save-profile-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default ProfilePage;
